// Pure helpers for EditMembershipPopover (PRODOPSAI-511 / US8;
// PRODOPSAI-523 useEntraTitle support).
//
// Module-level so SonarCloud rules S7721 + S3776 cannot fire and US10 can
// unit-test them without rendering React.

import type {
  ITeam,
  ITeamRole,
  IUpdateTeamMembershipRequest,
} from '@skystone/shared';
import {
  buildRoleOptions,
  buildTeamOptions,
  normaliseNote,
  SAME_AS_MY_TITLE_VALUE,
  type MappingRoleOption,
  type MappingTeamOption,
} from './add-mapping-helpers';

// Re-export the helpers the popover shares with the dialog so callers and
// tests do not need to know that they live in `add-mapping-helpers.ts`.
// `export { … } from './module'` direct re-export — never the
// `import X … ; export { X }` pattern flagged by SonarCloud rule S7763.
export {
  groupTeamsByRegionLabel,
  isConflictError,
  SAME_AS_MY_TITLE_VALUE,
  type MappingRoleOption,
  type MappingTeamOption,
} from './add-mapping-helpers';

/**
 * The four CRUD actions the popover exposes. Used to drive the inline
 * sub-flow state — only one is active at a time.
 */
export type EditAction =
  | 'menu'
  | 'change-role'
  | 'move-team'
  | 'edit-note';

/**
 * Helper teams + roles loader: both lists are filtered to non-archived rows
 * before being mapped to picker options. The popover reuses the dialog's
 * helpers — no separate dedupe needed.
 */
export function buildTeamPickerOptions(teams: readonly ITeam[]): MappingTeamOption[] {
  return buildTeamOptions(teams);
}

export function buildRolePickerOptions(roles: readonly ITeamRole[]): MappingRoleOption[] {
  return buildRoleOptions(roles);
}

/**
 * Build the PATCH body for a "change role" sub-flow. The wire shape always
 * carries the role binding atomically — never delete-then-insert.
 *
 * PRODOPSAI-523: the selection can be either a catalog `teamRoleId` (UUID)
 * or the SAME_AS_MY_TITLE sentinel (the pinned-top row in the picker). The
 * sentinel produces a `{teamRoleId: null, useEntraTitle: true}` patch so the
 * backend flips the membership over to the dynamic-title binding atomically.
 */
export function buildChangeRoleBody(pickerValue: string): IUpdateTeamMembershipRequest {
  if (pickerValue === SAME_AS_MY_TITLE_VALUE) {
    return { teamRoleId: null, useEntraTitle: true };
  }
  return { teamRoleId: pickerValue, useEntraTitle: false };
}

export function buildMoveTeamBody(teamId: string): IUpdateTeamMembershipRequest {
  return { teamId };
}

export function buildEditNoteBody(noteInput: string): IUpdateTeamMembershipRequest {
  return { note: normaliseNote(noteInput) };
}
